import { supabase } from '../../lib/supabase';
import type {
  AvailabilitySlot,
  Booking,
  ScheduleException,
  ScheduleHours,
  StaffLocation,
  StaffService
} from '../../lib/types';

export async function listSchedulingConfiguration(organisationId: string) {
  const [staffLocations, staffServices, locationHours, staffHours, exceptions] = await Promise.all([
    supabase.from('staff_locations').select('*').eq('organisation_id', organisationId),
    supabase.from('staff_services').select('*').eq('organisation_id', organisationId),
    supabase.from('location_operating_hours').select('*').eq('organisation_id', organisationId),
    supabase.from('staff_working_hours').select('*').eq('organisation_id', organisationId),
    supabase
      .from('schedule_exceptions')
      .select('*')
      .eq('organisation_id', organisationId)
      .is('archived_at', null)
      .order('starts_at')
  ]);
  const error = [staffLocations, staffServices, locationHours, staffHours, exceptions].find(
    (result) => result.error
  )?.error;
  if (error) throw error;
  return {
    staffLocations: (staffLocations.data ?? []) as StaffLocation[],
    staffServices: (staffServices.data ?? []) as StaffService[],
    locationHours: (locationHours.data ?? []) as ScheduleHours[],
    staffHours: (staffHours.data ?? []) as ScheduleHours[],
    exceptions: (exceptions.data ?? []) as ScheduleException[]
  };
}

export async function assignStaffLocation(
  organisationId: string,
  staffId: string,
  locationId: string
) {
  const { error } = await supabase.from('staff_locations').upsert(
    {
      organisation_id: organisationId,
      staff_id: staffId,
      location_id: locationId,
      is_active: true
    },
    { onConflict: 'organisation_id,staff_id,location_id' }
  );
  if (error) throw error;
}

export async function assignStaffService(
  organisationId: string,
  staffId: string,
  serviceId: string
) {
  const { error } = await supabase.from('staff_services').upsert(
    {
      organisation_id: organisationId,
      staff_id: staffId,
      service_id: serviceId,
      is_active: true
    },
    { onConflict: 'organisation_id,staff_id,service_id' }
  );
  if (error) throw error;
}

export async function addOperatingHours(
  organisationId: string,
  locationId: string,
  weekday: number,
  start: string,
  end: string
) {
  const { error } = await supabase.from('location_operating_hours').insert({
    organisation_id: organisationId,
    location_id: locationId,
    iso_weekday: weekday,
    local_start: start,
    local_end: end
  });
  if (error) throw error;
}

export async function addWorkingHours(
  organisationId: string,
  locationId: string,
  staffId: string,
  weekday: number,
  start: string,
  end: string
) {
  const { error } = await supabase.from('staff_working_hours').insert({
    organisation_id: organisationId,
    location_id: locationId,
    staff_id: staffId,
    iso_weekday: weekday,
    local_start: start,
    local_end: end
  });
  if (error) throw error;
}

export async function addScheduleException(input: {
  organisation_id: string;
  location_id: string | null;
  staff_id: string | null;
  kind: ScheduleException['kind'];
  starts_at: string;
  ends_at: string;
  reason: string;
}) {
  const { error } = await supabase.from('schedule_exceptions').insert(input);
  if (error) throw error;
}

export async function getAvailability(
  organisationId: string,
  locationId: string,
  serviceId: string,
  rangeStart: string,
  rangeEnd: string,
  staffId?: string
): Promise<AvailabilitySlot[]> {
  const response = (await supabase.rpc('get_available_slots', {
    requested_organisation_id: organisationId,
    requested_location_id: locationId,
    requested_service_id: serviceId,
    range_start: rangeStart,
    range_end: rangeEnd,
    requested_staff_id: staffId || undefined,
    slot_interval_minutes: 15
  })) as unknown as { data: AvailabilitySlot[] | null; error: Error | null };
  if (response.error) throw response.error;
  return response.data ?? [];
}

export async function createManualBooking(slot: AvailabilitySlot) {
  const { error } = await supabase.rpc('create_manual_booking', {
    requested_organisation_id: slot.organisation_id,
    requested_location_id: slot.location_id,
    requested_service_id: slot.service_id,
    requested_staff_id: slot.staff_id,
    requested_start: slot.starts_at,
    requested_request_id: crypto.randomUUID()
  });
  if (error) throw error;
}

export async function listBookings(organisationId: string, rangeStart: string): Promise<Booking[]> {
  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .eq('organisation_id', organisationId)
    .gte('starts_at', rangeStart)
    .order('starts_at')
    .limit(100);
  if (error) throw error;
  return (data ?? []) as Booking[];
}
